import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useGetOverviewQuery } from 'services/exploreService';
import { BarList, Columns, Empty, Kpi, PageHead, PairedBars, Panel, Skeleton, fmt, pct } from 'components/explore';

const qs = (o: Record<string, string>) => `?${new URLSearchParams(o).toString()}`;

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isFetching, error, refetch } = useGetOverviewQuery();

  if (error) {
    return (
      <div className="ax-page">
        <PageHead eyebrow="Atlas · Data warehouse" title="Overview" />
        <Alert
          type="error"
          showIcon
          message="Could not load warehouse overview"
          description="The Explorer API did not respond. Check that the Atlas API is running and that you are signed in."
          action={<Button onClick={() => refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  const t = data?.totals;
  const cov = data?.label_coverage ?? [];
  const covTotal = t?.dicoms ?? 0;
  const agree = data?.view_agreement;
  const views = (data?.by_view ?? []).slice(0, 18);
  const sources = data?.by_datasource ?? [];
  const scanners = (data?.by_manufacturer ?? []).slice(0, 12);
  const years = (data?.by_year ?? []).filter(y => y.year !== 'Unknown');
  const unknownYears = (data?.by_year ?? []).find(y => y.year === 'Unknown')?.studies ?? 0;

  return (
    <div className="ax-page">
      <PageHead
        title="Warehouse overview"
        lede="Studies, DICOMs and annotations across every source. Click any bar to open the matching studies or DICOMs."
        actions={
          <Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()} size="small">
            {data ? `Updated ${new Date(data.generated_at).toLocaleTimeString()}` : 'Refresh'}
          </Button>
        }
      />

      <section className="ax-tiles" aria-label="Totals">
        {isLoading || !t ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div className="ax-tile" key={i}>
              <Skeleton h={30} w="60%" />
              <Skeleton h={12} w="80%" />
            </div>
          ))
        ) : (
          <>
            <Kpi value={fmt(t.studies)} label="Studies" color="var(--s1)" onClick={() => navigate('/studies')} />
            <Kpi value={fmt(t.dicoms)} label="DICOMs" color="var(--s1)" onClick={() => navigate('/dicoms')} />
            <Kpi value={fmt(t.patients)} label="Patients" color="var(--line)" />
            <Kpi
              value={fmt(t.studies_with_report)}
              label="Studies with a report"
              sub={pct(t.studies_with_report, t.studies)}
              color="var(--s3)"
              onClick={() => navigate(`/studies${qs({ has_report: 'true' })}`)}
            />
            <Kpi value={fmt(t.datasources)} label="Data sources" color="var(--line)" />
          </>
        )}
      </section>

      <div className="ax-grid-2">
        <Panel
          eyebrow="Annotation coverage"
          title="DICOMs with each kind of label"
          sub={`Share of all ${fmt(covTotal)} DICOMs. Click a row to browse them.`}
        >
          {isLoading ? (
            <Skeleton h={120} />
          ) : (
            <BarList
              ariaLabel="Annotation coverage"
              color="var(--s3)"
              max={covTotal}
              items={cov.map(c => ({
                key: c.key,
                label: c.label,
                value: c.dicoms,
                secondary: pct(c.dicoms, covTotal),
                onClick: () => navigate(`/dicoms${qs({ label: c.key })}`),
              }))}
            />
          )}
          {agree && agree.both > 0 && (
            <p className="ax-note">
              Where a DICOM has both a tech view label and an AI view prediction, they agree on{' '}
              <b>{pct(agree.agree, agree.both)}</b> ({fmt(agree.agree)} of {fmt(agree.both)}).
            </p>
          )}
        </Panel>

        <Panel eyebrow="Views" title="DICOMs per view" sub="AI prediction (whitelisted view pipelines) and tech label, top 18 views.">
          {isLoading ? (
            <Skeleton h={220} />
          ) : views.length ? (
            <PairedBars
              aName="AI prediction"
              bName="Tech label"
              rows={views.map(v => ({
                key: v.view,
                label: v.view,
                a: v.predicted,
                b: v.labeled,
                onClick: () => navigate(`/dicoms${qs({ view: v.view })}`),
              }))}
            />
          ) : (
            <Empty title="No view predictions or labels" />
          )}
        </Panel>
      </div>

      <div className="ax-grid-2">
        <Panel eyebrow="Sources" title="Studies by data source" sub="DICOM counts come from each study's recorded number_of_dicoms.">
          {isLoading ? (
            <Skeleton h={160} />
          ) : (
            <BarList
              ariaLabel="Studies by data source"
              items={sources.map(s => ({
                key: s.id ?? s.name,
                label: s.name,
                value: s.studies,
                secondary: `${fmt(s.dicoms)} dcm`,
                onClick: s.id ? () => navigate(`/studies${qs({ datasource: s.id! })}`) : undefined,
              }))}
            />
          )}
        </Panel>

        <Panel eyebrow="Scanners" title="DICOMs by manufacturer and model" sub="Top 12 scanner models.">
          {isLoading ? (
            <Skeleton h={160} />
          ) : (
            <BarList
              ariaLabel="DICOMs by scanner"
              items={scanners.map(s => ({
                key: `${s.manufacturer}|${s.model}`,
                label: s.model,
                hint: s.manufacturer,
                value: s.dicoms,
                onClick:
                  s.manufacturer !== 'Unknown'
                    ? () =>
                        navigate(
                          `/dicoms${qs(
                            s.model !== 'Unknown'
                              ? { manufacturer: s.manufacturer, model: s.model }
                              : { manufacturer: s.manufacturer }
                          )}`
                        )
                    : undefined,
              }))}
            />
          )}
        </Panel>
      </div>

      <div className="ax-grid-2">
        <Panel
          eyebrow="Time"
          title="Studies by visit year"
          sub={unknownYears ? `${fmt(unknownYears)} studies have no visit year and are not shown.` : undefined}
        >
          {isLoading ? (
            <Skeleton h={160} />
          ) : (
            <Columns
              unit="studies"
              items={years.map(y => ({
                key: y.year,
                label: y.year,
                value: y.studies,
                onClick: () => navigate(`/studies${qs({ year: y.year })}`),
              }))}
            />
          )}
        </Panel>
        <Panel eyebrow="Modes" title="DICOMs by type">
          {isLoading ? (
            <Skeleton h={160} />
          ) : (
            <BarList
              ariaLabel="DICOMs by type"
              items={(data?.by_type ?? []).map(x => ({
                key: x.type,
                label: x.type,
                value: x.dicoms,
                secondary: pct(x.dicoms, covTotal),
                onClick: x.type !== 'Unknown' ? () => navigate(`/dicoms${qs({ type: x.type })}`) : undefined,
              }))}
            />
          )}
        </Panel>
      </div>
    </div>
  );
};

export default Overview;
